import { AlertTriangle, MinusCircle } from 'lucide-react';

import { cn } from '@/lib/utils';
import type { ImportResult, ImportRowResult } from '@/types/institute';
import type { BulkImportConfig } from './BulkImportPanel';

/**
 * The students the sheet did not admit, said plainly.
 *
 * The results table below this lists every row, and on a sheet of two hundred
 * that is exactly where four rejected children go unnoticed — the office sees
 * "183 admitted", closes the dialog, and finds out in November that Aarav was
 * never on the roll. So the ones that did *not* go in are lifted out and named
 * here, above the table, before anything else.
 *
 * Rejected and skipped are kept apart because they are different news. A
 * rejection is a mistake in the sheet and needs the office to do something. A
 * skip is the importer working correctly — that child is already on the roll,
 * which is what happens every time somebody fixes one row and re-uploads the
 * whole file.
 */

export interface ImportRejectionListProps {
  result: ImportResult;
  statusFilter?: 'all' | 'ADMITTED' | 'REJECTED' | 'SKIPPED';
  /** Who the rows are, so the headline names them — students or staff. */
  config: Pick<BulkImportConfig, 'noun' | 'done' | 'alreadyThere'>;
}

function RowLine({ row, tone }: { row: ImportRowResult; tone: 'critical' | 'warning' }) {
  return (
    <li className="flex items-start gap-3 rounded-2xl px-2.5 py-2">
      <span
        className={cn(
          'mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-lg font-mono text-[10px] font-black tabular-nums',
          tone === 'critical'
            ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300'
            : 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300',
        )}
        // The row number is read out as part of the sentence below, so it is
        // not announced twice.
        aria-hidden
      >
        {row.row}
      </span>

      <span className="min-w-0 flex-1">
        <span
          className={cn(
            'block truncate text-[13px] font-extrabold',
            tone === 'critical'
              ? 'text-rose-900 dark:text-rose-200'
              : 'text-amber-900 dark:text-amber-200',
          )}
        >
          {row.name}
          <span className="ml-1.5 font-mono text-[10.5px] font-bold opacity-70">
            row {row.row}
          </span>
        </span>
        {row.reason && (
          <span
            className={cn(
              'mt-0.5 block text-[11.5px] font-medium',
              tone === 'critical'
                ? 'text-rose-700/90 dark:text-rose-300/90'
                : 'text-amber-700/90 dark:text-amber-300/90',
            )}
          >
            {row.reason}
          </span>
        )}
      </span>
    </li>
  );
}

export function ImportRejectionList({
  result,
  statusFilter = 'all',
  config,
}: ImportRejectionListProps) {
  const rejected = result.rows.filter((row) => row.status === 'REJECTED');
  const skipped = result.rows.filter((row) => row.status === 'SKIPPED');

  const showRejected =
    (statusFilter === 'all' || statusFilter === 'REJECTED') && rejected.length > 0;
  const showSkipped =
    (statusFilter === 'all' || statusFilter === 'SKIPPED') && skipped.length > 0;

  if (!showRejected && !showSkipped) return null;

  return (
    <div className="flex flex-col gap-3">
      {showRejected && (
        <div
          // Announced when it appears: an office watching the spinner has no
          // other way of being told four children did not go in.
          role="alert"
          className="animate-slide-down overflow-hidden rounded-2xl border border-rose-200 dark:border-rose-900/50 bg-rose-50/80 dark:bg-rose-950/30"
        >
          <div className="flex items-center gap-3 border-b border-rose-200/70 dark:border-rose-900/50 px-3.5 py-3">
            <span
              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-rose-500 to-rose-600 text-white shadow-md shadow-rose-500/25"
              aria-hidden
            >
              <AlertTriangle className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h3 className="text-[14px] font-black tracking-tight text-rose-900 dark:text-rose-200">
                {rejected.length}{' '}
                {rejected.length === 1
                  ? `${config.noun.one} was not ${config.done}`
                  : `${config.noun.many} were not ${config.done}`}
              </h3>
              <p className="mt-0.5 text-[11.5px] font-medium text-rose-700/90 dark:text-rose-300/90">
                Nothing was saved for these rows. Correct them in the sheet and upload it again.
              </p>
            </div>
          </div>

          <ul className="flex flex-col gap-0.5 p-1.5">
            {rejected.map((row) => (
              <RowLine key={row.row} row={row} tone="critical" />
            ))}
          </ul>
        </div>
      )}

      {skipped.length > 0 && (
        <div className="overflow-hidden rounded-2xl border border-amber-200 dark:border-amber-900/50 bg-amber-50/70 dark:bg-amber-950/25">
          <div className="flex items-center gap-3 border-b border-amber-200/70 dark:border-amber-900/50 px-3.5 py-3">
            <span
              className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-md shadow-amber-500/25"
              aria-hidden
            >
              <MinusCircle className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <h3 className="text-[14px] font-black tracking-tight text-amber-900 dark:text-amber-200">
                {skipped.length} {skipped.length === 1 ? 'row was' : 'rows were'} skipped
              </h3>
              <p className="mt-0.5 text-[11.5px] font-medium text-amber-700/90 dark:text-amber-300/90">
                {config.alreadyThere.charAt(0).toUpperCase() + config.alreadyThere.slice(1)}.
                Nothing is wrong — this is what stops a re-upload adding anybody twice.
              </p>
            </div>
          </div>

          <ul className="flex flex-col gap-0.5 p-1.5">
            {skipped.map((row) => (
              <RowLine key={row.row} row={row} tone="warning" />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
